/*
 * IMPORTANT: We use /fp-versions of date-fns, so all arguments are in the opposite order to the standard docs.
 * See: https://date-fns.org/v2.22.1/docs/FP-Guide
 */

import {
    addDays,
    addMonths,
    differenceInCalendarDays,
    differenceInYears,
    endOfDay,
    formatISOWithOptions,
    formatWithOptions,
    isAfter,
    isBefore,
    isValid,
    parse,
    parseISO,
    startOfDay,
    subDays,
} from 'date-fns/fp';
import { nb } from 'date-fns/locale';

export {
    addDays,
    addHours,
    addMinutes,
    addMonths,
    addSeconds,
    differenceInCalendarDays,
    differenceInMinutes,
    endOfDay,
    endOfMonth,
    getDay,
    getISOWeek,
    isAfter,
    isBefore,
    isSameDay,
    isValid,
    isWithinInterval,
    max,
    nextDay,
    parseISO,
    setDate,
    startOfDay,
    startOfMonth,
    subDays,
    subMonths,
} from 'date-fns/fp';

// Type check
export const isDate = (obj: unknown): boolean => obj instanceof Date || typeof obj === 'number';

// Relative dates
export const isPast: (date: Date | number) => boolean = isBefore(startOfDay(Date.now()));
export const isFuture: (date: Date | number) => boolean = isAfter(endOfDay(subDays(1, Date.now())));
export const isFutureRange = (to: Date | number, from: Date | number): boolean =>
    isDate(from) && isDate(to) && isFuture(from) && isAfter(from, to);
export const getOneYearFromToday = (): Date => addMonths(12, new Date());

// Diff
export const isYoungerThan18 = (birthDate: Date): boolean => differenceInYears(birthDate, Date.now()) < 18;
export const travelDaysBetween = (start: Date | number, end: Date | number): number => differenceInCalendarDays(start, end) + 1;
// biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
export const safeDifferenceInCalendarDays = (other: any, date: any): number | undefined =>
    isDate(other) && isDate(date) ? differenceInCalendarDays(other, date) : undefined;

// Formatting
export const formatISODate: (date: Date | number) => string = formatISOWithOptions({ representation: 'date' }); // 2023-05-23
export const formatISODateTime: (date: Date | number) => string = formatWithOptions({}, "yyyy-MM-dd'T'HH:mm"); // 2023-05-23T15:00
export const formatClientDate: (date: Date | number) => string = formatWithOptions({}, 'dd.MM.yyyy'); // 23.05.2023
export const formatShortenedClientDate: (date: Date | number) => string = formatWithOptions({}, 'dd.MM.yy'); // 23.05.23
export const formatShortDate: (date: Date | number) => string = formatWithOptions({}, 'ddMMyy'); // 230523

export const formatReadableDate: (date: Date | number) => string = formatWithOptions({ locale: nb }, 'eee dd MMM'); // tir 16 nov
export const formatReadableDateWithYear: (date: Date | number) => string = formatWithOptions({ locale: nb }, "eee'.' dd MMM yyyy"); // tir 16 nov 2024
export const formatReadableDateShort: (date: Date | number) => string = formatWithOptions({ locale: nb }, 'eee dd.MM'); // lør 23.05
export const formatReadableDateShortNoWeekday: (date: Date | number) => string = formatWithOptions({ locale: nb }, 'dd. MMM'); // 16. nov
export const formatReadableDateShortWithYear: (date: Date | number) => string = formatWithOptions({ locale: nb }, 'dd. MMM yyyy'); // 16. nov 2021
export const formatReadableDateFull: (date: Date | number) => string = formatWithOptions({ locale: nb }, "eeee d'.' MMMM yyyy"); // tirsdag 16. november 2021
export const formatReadableDateFullNoYear: (date: Date | number) => string = formatWithOptions({ locale: nb }, "eeee d'.' MMMM"); // tirsdag 16. november
export const formatReadableMonthYear: (date: Date | number) => string = formatWithOptions({ locale: nb }, 'MMMM yyyy'); // november 2021
export const formatReadableDayTime: (date: Date | number) => string = formatWithOptions({ locale: nb }, 'eee HH:mm'); // tir 15:00
export const formatReadableTime: (date: Date | number) => string = formatWithOptions({ locale: nb }, 'HH:mm'); // 15:00

// Safe formatting (date-fns throws 'Invalid Date' error when passed weird data)
// biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
export const safeFormatClientDate = <T = string>(date: any, otherwise: string | T = ''): string | T =>
    isDate(date) ? formatClientDate(date) : otherwise;
// biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
export const safeFormatISODate = <T = string>(date: any, otherwise: string | T = ''): string | T =>
    isDate(date) ? formatISODate(date) : otherwise;
// biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
export const safeFormatReadableDate = <T = string>(date: any, otherwise: string | T = ''): string | T =>
    isDate(date) ? formatReadableDate(date) : otherwise;

// Safe parsing
// biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
export const safeParseClientDate = <T = undefined>(str: any, otherwise?: T): Date | T => {
    try {
        const date = parse(new Date(), 'dd.MM.yyyy', str);
        return isValid(date) ? date : (otherwise as T);
    } catch (_e) {
        return otherwise as T;
    }
};
// biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
export const safeParseISO = <T = undefined>(str: any, otherwise?: T): Date | T => {
    try {
        const date = parseISO(str);
        return isValid(date) ? date : (otherwise as T);
    } catch (_e) {
        return otherwise as T;
    }
};

// Test helpers
export const addDaysClient = (days: number): string => formatClientDate(addDays(days, new Date()));
export const addDaysServer = (days: number): string => formatISODate(addDays(days, new Date()));
